import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useAccount, useBalance } from "wagmi";
import { useWeb3Modal } from "@web3modal/wagmi/react";
import { FaTelegramPlane } from "react-icons/fa";
import nftBg from "../../assets/nft.avif";
import globe from "../../assets/globe.png";
import logo from "../../assets/logo.png";
import { HopePepe_Contract_Address } from "../../utilies/constant";

const KRIPTOM_URL =
  process.env.REACT_APP_KRIPTOM_URL || "https://kriptom.org/piyasa/";
const TELEGRAM_SUPPORT_URL = "https://t.me/bugrasukas";

const holderBenefits = [
  "Unlimited KRIPTOM indicator access for verified HopePepe holder wallets.",
  "A reserved KRIPTOM token allocation planned as a 12-month locked stake.",
  "Priority migration support before market liquidity and wider platform usage are opened."
];

const claimSteps = [
  "Connect the wallet that purchased or currently holds HopePepe.",
  "Create or sign in to your KRIPTOM account.",
  "Sign the holder claim message so KRIPTOM can verify the wallet on-chain.",
  "Use all KRIPTOM indicators without subscription limits after verification."
];

function formatWallet(value = "") {
  return value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "";
}

function formatBalance(value) {
  if (!value) return "0";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  if (numeric === 0) return "0";
  if (numeric < 0.000001) return "< 0.000001";
  return numeric.toLocaleString("en-US", {
    maximumFractionDigits: numeric > 1 ? 4 : 8
  });
}

const Home = () => {
  const { address, isConnected } = useAccount();
  const { open } = useWeb3Modal();
  const {
    data: hopeBalance,
    isError: balanceError,
    isLoading: balanceLoading
  } = useBalance({
    address,
    token: HopePepe_Contract_Address,
    watch: true,
    enabled: Boolean(address)
  });

  const holderState = useMemo(() => {
    const balance = Number(hopeBalance?.formatted || 0);
    if (!isConnected) {
      return {
        tone: "neutral",
        title: "Wallet not connected",
        detail: "Connect the HopePepe holder wallet to check access eligibility."
      };
    }
    if (balanceLoading) {
      return {
        tone: "neutral",
        title: "Checking wallet",
        detail: "Reading the HopePepe balance from Avalanche Fuji."
      };
    }
    if (balanceError) {
      return {
        tone: "warning",
        title: "Balance check needs attention",
        detail: "Switch to Avalanche Fuji or try again from the KRIPTOM claim flow."
      };
    }
    if (balance > 0) {
      return {
        tone: "success",
        title: "HopePepe holder detected",
        detail: "This wallet should be eligible for the KRIPTOM compensation access flow."
      };
    }
    return {
      tone: "warning",
      title: "No HopePepe balance detected",
      detail: "Use the wallet that purchased or still holds HopePepe before claiming."
    };
  }, [balanceError, balanceLoading, hopeBalance, isConnected]);

  return (
    <main className="min-h-screen bg-[#07140f] text-white">
      <section
        className="relative min-h-screen overflow-hidden pt-[104px]"
        style={{
          backgroundImage: `linear-gradient(120deg, rgba(3, 18, 13, 0.93), rgba(5, 23, 33, 0.86)), url(${nftBg})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="container mx-auto px-4 pb-12">
          <div className="grid items-center gap-10 py-10 lg:grid-cols-[1.05fr_0.95fr] lg:py-16">
            <div className="max-w-3xl">
              <div className="mb-5 inline-flex items-center gap-3 rounded-full border border-[#6ee7a4]/35 bg-[#10291d]/80 px-4 py-2 text-[13px] font-[600] text-[#b9ffd2]">
                <span className="h-2 w-2 rounded-full bg-[#53e28c]" />
                HopePepe migration notice
              </div>
              <h1 className="max-w-4xl text-[40px] font-[800] leading-[1.02] text-white sm:text-[54px] lg:text-[72px]">
                HopePepe is transitioning into KRIPTOM.org.
              </h1>
              <p className="mt-6 max-w-2xl text-[17px] leading-[1.75] text-[#d6e6de] lg:text-[20px]">
                The HopePepe treasury and operating runway were severely damaged
                during the AVAX price collapse. Instead of leaving early
                supporters without a path forward, we are moving the community
                into KRIPTOM.org and compensating verified HopePepe buyers with
                unlimited access to every KRIPTOM indicator.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a
                  href={KRIPTOM_URL}
                  className="flex h-[54px] items-center justify-center rounded-[8px] bg-[#4ade80] px-6 text-[16px] font-[800] text-[#052512] transition hover:bg-[#72f29c]"
                >
                  Open KRIPTOM.org
                </a>
                <a
                  href="#holder-access"
                  className="flex h-[54px] items-center justify-center rounded-[8px] border border-[#7dd3fc]/40 bg-[#08202d]/75 px-6 text-[16px] font-[700] text-[#dbf7ff] transition hover:border-[#7dd3fc]"
                >
                  Check holder access
                </a>
                <a
                  href={TELEGRAM_SUPPORT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-[54px] items-center justify-center gap-2 rounded-[8px] border border-[#58a6ff]/45 bg-[#071c2d]/80 px-6 text-[16px] font-[800] text-[#dcefff] transition hover:border-[#8bc5ff] hover:bg-[#0b2740]"
                >
                  <FaTelegramPlane aria-hidden="true" />
                  Message @bugrasukas
                </a>
              </div>
            </div>

            <div
              id="holder-access"
              className="rounded-[8px] border border-[#dafce8]/18 bg-[#071b13]/88 p-5 shadow-2xl shadow-black/30 backdrop-blur"
            >
              <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-5">
                <div className="flex items-center gap-3">
                  <img
                    src={logo}
                    className="h-[54px] w-[54px] rounded-[8px] bg-[#0f251a] object-contain p-2"
                    alt="HopePepe"
                  />
                  <div>
                    <p className="text-[13px] font-[700] uppercase tracking-[0.12em] text-[#89f5b1]">
                      Holder status
                    </p>
                    <h2 className="text-[22px] font-[800] text-white">
                      Compensation access
                    </h2>
                  </div>
                </div>
                <img src={globe} className="hidden h-[72px] sm:block" alt="" />
              </div>

              <div className="mt-5 rounded-[8px] bg-[#0e241a] p-4">
                <div
                  className={`mb-3 inline-flex rounded-full px-3 py-1 text-[12px] font-[800] ${
                    holderState.tone === "success"
                      ? "bg-[#1b5f33] text-[#b7ffd0]"
                      : holderState.tone === "warning"
                      ? "bg-[#5f491b] text-[#ffe6a7]"
                      : "bg-[#163447] text-[#b9ecff]"
                  }`}
                >
                  {holderState.title}
                </div>
                <p className="text-[15px] leading-[1.6] text-[#d7e6dc]">
                  {holderState.detail}
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-[8px] border border-white/10 bg-[#081611] p-3">
                    <p className="text-[12px] font-[700] uppercase text-[#81a794]">
                      Connected wallet
                    </p>
                    <p className="mt-1 break-all text-[15px] font-[700] text-white">
                      {address ? formatWallet(address) : "Not connected"}
                    </p>
                  </div>
                  <div className="rounded-[8px] border border-white/10 bg-[#081611] p-3">
                    <p className="text-[12px] font-[700] uppercase text-[#81a794]">
                      HopePepe balance
                    </p>
                    <p className="mt-1 text-[15px] font-[700] text-white">
                      {balanceLoading
                        ? "Checking..."
                        : `${formatBalance(hopeBalance?.formatted)} HOPEPEPE`}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => open()}
                  className="mt-4 flex h-[48px] w-full items-center justify-center rounded-[8px] bg-white text-[15px] font-[800] text-[#07140f] transition hover:bg-[#dfffea]"
                >
                  {address ? "Manage connected wallet" : "Connect holder wallet"}
                </button>
              </div>

              <div className="mt-5">
                <h3 className="text-[16px] font-[800] text-white">
                  What verified holders receive
                </h3>
                <div className="mt-3 grid gap-3">
                  {holderBenefits.map((benefit) => (
                    <div
                      key={benefit}
                      className="rounded-[8px] border border-[#7ceca6]/18 bg-[#0c1f17] p-3 text-[14px] leading-[1.55] text-[#d8efe1]"
                    >
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 border-t border-white/10 pt-7 lg:grid-cols-4">
            {claimSteps.map((step, index) => (
              <div
                key={step}
                className="rounded-[8px] border border-white/10 bg-[#071811]/82 p-4"
              >
                <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#4ade80] text-[14px] font-[900] text-[#042411]">
                  {index + 1}
                </div>
                <p className="text-[14px] leading-[1.6] text-[#dbece2]">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-[#153827] bg-[#06100c] px-4 py-12">
        <div className="container mx-auto grid gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="text-[13px] font-[800] uppercase tracking-[0.14em] text-[#64de92]">
              Transition record
            </p>
            <h2 className="mt-3 text-[30px] font-[800] leading-[1.08] text-white lg:text-[42px]">
              A direct path for the original community.
            </h2>
          </div>
          <div className="grid gap-4 text-[15px] leading-[1.75] text-[#d4e8dc]">
            <p>
              HopePepe is no longer being positioned as a standalone DeFi
              launch. The practical next step is to move holders into KRIPTOM,
              a market terminal focused on scanners, indicators, alerts, and
              trading workflow tools.
            </p>
            <p>
              Holder verification is based on wallet ownership and server-side
              on-chain balance checks. Browser screenshots or manually reported
              balances are not enough; the KRIPTOM claim flow requires a signed
              wallet message and confirms the HopePepe token balance directly.
            </p>
            <p className="rounded-[8px] border border-[#facc15]/30 bg-[#201b09] p-4 text-[#ffeeb5]">
              KRIPTOM token allocations are intended as locked compensation
              grants, not a profit guarantee. Final distribution, staking, and
              liquidity timing can be adjusted for legal, security, and market
              readiness requirements.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#07140f] px-4 py-12">
        <div className="container mx-auto flex flex-col gap-4 rounded-[8px] border border-[#4ade80]/25 bg-[#0b1e16] p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-[24px] font-[800] text-white">
              Already a holder?
            </h2>
            <p className="mt-2 max-w-2xl text-[15px] leading-[1.6] text-[#cfe4d7]">
              Keep the holder wallet available, sign in to KRIPTOM, and claim
              unlimited indicator access from the account flow.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href={KRIPTOM_URL}
              className="flex h-[48px] items-center justify-center rounded-[8px] bg-[#4ade80] px-5 text-[15px] font-[800] text-[#041e0e]"
            >
              Go to KRIPTOM
            </a>
            <a
              href={TELEGRAM_SUPPORT_URL}
              target="_blank"
              rel="noreferrer"
              className="flex h-[48px] items-center justify-center gap-2 rounded-[8px] border border-[#58a6ff]/45 bg-[#071c2d] px-5 text-[15px] font-[800] text-[#e1f0ff]"
            >
              <FaTelegramPlane aria-hidden="true" />
              Telegram questions
            </a>
            <NavLink
              to="/trade"
              className="flex h-[48px] items-center justify-center rounded-[8px] border border-white/15 px-5 text-[15px] font-[700] text-white"
            >
              Legacy DEX
            </NavLink>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
